import React from 'react'

import { Stack } from '@mui/material'

import { Skeleton, Tabs } from '@repo/ui'
import { Header, InfoBlock, InfoItem, InfoRow } from 'components'

/**
 * Loading placeholder for the internal company detail page (§19).
 *
 * ## What it replaces
 *
 * `if (isLoading) return <Loader />` — a centred spinner in place of a 133px header, a 44px
 * segmented-tab bar and a 170px details panel.
 *
 * ## Measured, not guessed
 *
 * Off `/internal/companies/:id` (viewport 1711, content column x=280 w=1407, Details tab):
 *
 *   wayfinding row   y=24  h=33
 *   title row        y=69  h=64   — 64 because of the 64px company avatar, not the title's 31
 *   avatar           64x64 at x=280; title 143x31 at y=85; status chip 65x32 at x=511
 *   header actions   Open in 117 / Go to space 143 / Suspend 121 / kebab 40, all h=40
 *                    ("Go to space" was "Go into space" at 156 until the label was
 *                     aligned across all three surfaces; re-measured, not scaled)
 *   header total     y=24  h=133
 *   tab section      y=157 h=250  (`pt: 3` then `spacing: 6`)
 *   segmented bar    y=169 h=44   — Details 76 / Plan 59 / Invoices 82 / Orders 76 / Users 67
 *   details panel    y=237 h=170  — two InfoRows of 84
 *
 * ⚠️ Two of those figures are STALE as of VRG-516 and were NOT re-measured — they are
 * left as the last real measurement rather than replaced with a guess, since the whole
 * point of this block is that the numbers came off a browser:
 *   - the `Plan 59` tab width — the label is now "Billing", so it is wider;
 *   - `details panel h=170` — the Details tab now carries FIVE InfoRows, not two.
 * Neither drives layout here (see Construction: the tab bar is the real control and the
 * panel is the real `InfoBlock`, so both size themselves), so the skeleton is correct by
 * construction while the comment is merely out of date. Re-measure when next in the page.
 *
 * ## Construction
 *
 * The tab bar is the REAL `SegmentedTabs` with the real five tabs — they are static labels, so its
 * 44px height, its pill geometry and the tab widths are identical rather than approximated. The
 * details panel is the real `InfoBlock`/`InfoRow`/`InfoItem` with `value={undefined}`, which
 * `InfoItem` already draws as a `<Skeleton width={150} />`, so its 170px is right by construction
 * and survives a field being added to either row. The avatar and status chip are `Header`'s own
 * placeholders (`avatar={null}` / `status={null}`), which is what keeps the title row at 64.
 *
 * Only the four action-button widths are literals, and none of them affects vertical footprint (the
 * title row carries its own `minHeight: 49`, and the avatar makes it 64 regardless).
 *
 * ## Where it deliberately under-reserves
 *
 * The header's action set is the most state-dependent part of this page: `Go into space` renders only
 * for a platform admin, `Suspend` flips to `Unsuspend`, and an archived company swaps several of
 * them. The common case — an active company viewed by a platform admin — is what is reserved. The
 * two conditional banners above the tabs (subscription-cancelling, suspended) are not reserved at
 * all; they are exceptional states, and reserving one would hole every healthy company's page.
 */
export const CompanyPageSkeleton = () => {
  /** A header action button, at the 40px height `headerActionGroupSx` enforces. */
  const ActionSkeleton = ({ width }: { width: number }) => (
    <Skeleton variant="rounded" width={width} height={40} sx={{ borderRadius: 2 }} />
  )

  return (
    <>
      <Header
        breadcrumbs={true}
        // A bar, NOT omitted: `Header` renders the trail only when this is truthy, and omitting it
        // also trips the branch that pulls the whole header up 24.5px.
        breadcrumbsOveride={<Skeleton width={150} height={16} />}
        // 143px is the measured width of a representative company name.
        title={<Skeleton width={143} height={31} />}
        // `null`, not omitted — Header's own 64px circular placeholder is what holds the title row
        // at 64 instead of collapsing it to 49.
        avatar={null as any}
        status={null as any}
        rightComponents={
          <Stack direction="row" spacing={3} alignItems="center">
            <ActionSkeleton width={117} />
            {/* "Go to space" — measured 143 in-browser after the label was aligned
                from "Go into space" (156). §19 is footprint parity: a stale width
                here is a 13px layout shift the moment the real header paints. */}
            <ActionSkeleton width={143} />
            <ActionSkeleton width={121} />
            <ActionSkeleton width={40} />
          </Stack>
        }
      />

      {/* Same `pt: 3` / `spacing: 6` as the loaded page. */}
      <Stack pt={3} spacing={6}>
        {/* The real control on the real tabs — static labels, so this is exact, not approximate.
            VRG-470: converted in LOCKSTEP with CompanyPage. §19 is footprint parity, and the two
            controls are different heights (pill track 48, underline row 52) — a skeleton left on
            the pill would shift the whole page by 4px on every load. */}
        <Tabs
          redirect={false}
          value="details"
          onChange={() => {}}
          tabs={[
            { label: 'Details', value: 'details' },
            // VRG-516: 'Billing', matching CompanyPage. The VALUE stays `plan` there,
            // but the LABEL is what sets the tab's width, so a stale 'Plan' here would
            // shift the row horizontally the instant the real tabs paint.
            { label: 'Billing', value: 'plan' },
            { label: 'Invoices', value: 'invoices' },
            { label: 'Orders', value: 'orders' },
            { label: 'Users', value: 'users' },
          ]}
        />

        <Stack spacing={4}>
          {/* VRG-516 — `columns={4}` and the 4 | 4 | 4 | 3 | 2 row composition mirror
              CompanyPage EXACTLY, including "Join date" sitting in the second row
              rather than the first. §19 is footprint parity: the grid changes which
              column each field lands in, so a skeleton still laying them out
              `space-between` would slide every field sideways the instant the real
              panel paints — a horizontal shift rather than the vertical one the row
              count guards against, but the same defect.

              Each InfoRow is a fixed 84px `minHeight` regardless of how many items it
              holds, so omitting a row is a 84px layout shift, not a cosmetic gap. */}
          <InfoBlock>
            <InfoRow columns={4}>
              <InfoItem label="Users" value={undefined} />
              <InfoItem label="Certificates" value={undefined} />
              <InfoItem label="Owners" value={undefined} />
              <InfoItem label="Orders" value={undefined} />
            </InfoRow>
            <InfoRow columns={4}>
              <InfoItem label="Join date" value={undefined} />
              <InfoItem label="Headquarters" value={undefined} />
              <InfoItem label="Subdomain" value={undefined} />
              <InfoItem label="Signup domain" value={undefined} />
            </InfoRow>
            <InfoRow columns={4}>
              <InfoItem label="Kind" value={undefined} />
              <InfoItem label="Tier" value={undefined} />
              <InfoItem label="Provisioning" value={undefined} />
              <InfoItem label="Provider" value={undefined} />
            </InfoRow>
            <InfoRow columns={4}>
              <InfoItem label="Reporting currency" value={undefined} />
              <InfoItem label="Employee count" value={undefined} />
              <InfoItem label="Categories" value={undefined} />
            </InfoRow>
            <InfoRow columns={4}>
              <InfoItem label="Support email" value={undefined} />
              <InfoItem label="Support phone" value={undefined} />
            </InfoRow>
          </InfoBlock>
        </Stack>
      </Stack>
    </>
  )
}
